/**
 * React Query hook for fetching stories
 * Story 198: View and filter stories in backlog
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/** Story list item from API */
export interface StoryListItem {
  id: string;
  external_id: string;
  title: string;
  status: 'proposed' | 'in-progress' | 'qa' | 'done' | 'blocked' | 'paused';
  priority: number;
  epic_slug: string | null;
  assignee: string | null;
}

/** Filter options for stories query */
export interface StoryFilters {
  status?: string[];
  search?: string;
}

/** API response for stories list - Story 278 */
export interface StoriesResponse {
  stories: StoryListItem[];
  hiddenByFilter: number;
}

/** Query key factory for stories */
export const storyKeys = {
  all: ['stories'] as const,
  list: (projectSlug: string) => [...storyKeys.all, 'list', projectSlug] as const,
  filtered: (projectSlug: string, filters: StoryFilters) =>
    [...storyKeys.list(projectSlug), filters] as const,
};

/** Fetch stories for a project with optional filters */
async function fetchStories(
  projectSlug: string,
  filters: StoryFilters
): Promise<StoriesResponse> {
  const params = new URLSearchParams();

  if (filters.status?.length) {
    params.set('status', filters.status.join(','));
  }

  if (filters.search) {
    params.set('q', filters.search);
  }

  const queryString = params.toString();
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories${
    queryString ? `?${queryString}` : ''
  }`;

  const data = await api.fetch(endpoint);
  return {
    stories: data.stories,
    hiddenByFilter: data.hiddenByFilter ?? 0,
  };
}

/**
 * Hook to fetch stories with optional filters
 *
 * @param projectSlug - Project identifier (e.g., "boxabirds/claude-backlog")
 * @param filters - Optional status and search filters
 */
export function useStories(projectSlug: string, filters: StoryFilters = {}) {
  return useQuery({
    queryKey: storyKeys.filtered(projectSlug, filters),
    queryFn: () => fetchStories(projectSlug, filters),
    enabled: !!projectSlug,
  });
}
